import * as Sentry from "@sentry/react";

import {
  getCollectionOfDittoComponents,
  useTexts,
} from "Components/TextContext";
import {
  MAX_AGE_AKA_HEALTH_INSURANCE_CUSTOMER,
  MIN_AGE_AKA_HEALTH_INSURANCE_CUSTOMER,
} from "Features/CheckPrice/Products/productConstants";
import { Form, FormikProps } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { Icons } from "@storebrand-digital/insurance-definitions";
import { MarketingConsent } from "Components/Consent/MarketingConsent";
import { ProductIntroTile } from "Components/Features/CheckPrice/Tiles/ProductIntroTile";
import { PartnerProductIntroForms } from "Components/Features/CheckPrice/Tiles/ProductIntroTile/PartnerProductIntroForms/PartnerProductIntroForms";
import { AlertWithIllustration } from "Components/Form/Alert";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { IllustrationName } from "Constants/constants";
import { ProductBackendName } from "Features/CheckPrice/Products/productTypes";
import { useAkaHealthConficSelector } from "Store/akaHealthConfig/akaHealthConfigHooks";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { usePriceCalculationSelector } from "Store/priceCalculation/priceCalculationSelectors";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { nextStep } from "Store/steps/stepsSlice";
import { customerDateOfBirthSelector } from "Store/userIdentification/userIdentificationSelectors";
import { mapValidationErrorsDitto } from "Utils/formUtils";
import { useUnleashFeatureFlag } from "Utils/unleash/flagHooks";
import { FeatureFlagTask } from "Utils/unleash/flags";
import React from "react";
import { customerIsWithinAgeRange } from "../../../RiskProducts/riskProductsUtils";
import { AKA_HEALTH_ABOUT_YOU_STEP } from "../akaHealthSteps";
import { AkaHealthProductIntroFormElements } from "./AkaHealthProductIntroFormElements";
import { AKA_HEALTH_PRODUCT_INTRO_INITIAL_FORM_VALUES } from "./akaHealthProductIntroConstants";
import { AkaHealthProductIntroFormValues } from "./akaHealthProductIntroInterfaces";
import { canCustomerPurchaseMemberInsurance } from "./akaHealthProductIntroUtils";
import { validationSchema } from "./akaHealthProductIntroValidation";

export const AkaHealthProductIntro = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { getText } = useTexts();
  const { priceCalculationResult } = usePriceCalculationSelector();
  const initialFormState = useFormState<AkaHealthProductIntroFormValues>(
    AKA_HEALTH_ABOUT_YOU_STEP.id,
    AKA_HEALTH_PRODUCT_INTRO_INITIAL_FORM_VALUES,
  );

  const showAlreadyHasProductAlertToggle = useUnleashFeatureFlag(
    FeatureFlagTask.SSX_821_AKA_HEALTH_ALREADY_HAS_PRODUCT,
  );

  const akaHealthConfig = useAkaHealthConficSelector();
  const customerDateOfBirth = useSelector(customerDateOfBirthSelector);

  const stopCustomerWithRegisteredInsurance =
    !canCustomerPurchaseMemberInsurance(akaHealthConfig) &&
    showAlreadyHasProductAlertToggle;

  const isCustomerWithinAgeRange = customerIsWithinAgeRange(
    customerDateOfBirth,
    MAX_AGE_AKA_HEALTH_INSURANCE_CUSTOMER,
    MIN_AGE_AKA_HEALTH_INSURANCE_CUSTOMER,
  );

  const { shorttitle: shortTitleComponents } = getCollectionOfDittoComponents(
    TextComponentNameLevel1.HEALTH,
  );

  const handleSubmit = (form: AkaHealthProductIntroFormValues): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: AKA_HEALTH_ABOUT_YOU_STEP.id,
        formState: form,
      }),
    );

    dispatch(nextStep());
  };

  const renderContent = (
    formProps: FormikProps<AkaHealthProductIntroFormValues>,
  ): React.ReactElement => {
    if (stopCustomerWithRegisteredInsurance) {
      return (
        <AlertWithIllustration
          title={getText(
            "akahealthform.alerttitle.stopcustomerwithregisteredinsurance",
          )}
          description={getText(
            "akahealthform.alertdescription.stopcustomerwithregisteredinsurance",
          )}
          iconName={Icons.HEALTH}
          marginBottom="32px"
        />
      );
    }

    if (!isCustomerWithinAgeRange) {
      return (
        <AlertWithIllustration
          title={getText("akahealthform.alerttitle.customernotwithinagerange")}
          iconName={Icons.HEALTH}
          marginBottom="32px"
        />
      );
    }

    return (
      <>
        <AkaHealthProductIntroFormElements formProps={formProps} />
        <PartnerProductIntroForms />
        <MarketingConsent />
        {priceCalculationResult.error && (
          <SystemErrorAlert alertId="priceCalculation" />
        )}
        <ScrollToFirstError />
        <ErrorsSummedUp
          errors={mapValidationErrorsDitto(
            formProps.errors,
            shortTitleComponents,
          )}
        />
      </>
    );
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductIntro - ${INSURANCE_NAME_VALUE.AKA_HEALTH_WITHOUT_SIGNING}`,
        );
      }}
    >
      <ProductIntroTile
        illustrationName={IllustrationName.ESTABLISHMENT_FAMILY}
        productBackendName={ProductBackendName.AKA_HEALTH}
        validationSchema={validationSchema()}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
      >
        {(formProps: FormikProps<AkaHealthProductIntroFormValues>) => {
          return (
            <Form>
              {renderContent(formProps)}

              <NavigationButtons
                hidePreviousButton
                isLoading={priceCalculationResult.isLoading}
                disableNextButton={
                  !isCustomerWithinAgeRange ||
                  stopCustomerWithRegisteredInsurance
                }
              />
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.AKA_HEALTH_WITHOUT_SIGNING}
                stepId={AKA_HEALTH_ABOUT_YOU_STEP.id}
              />
            </Form>
          );
        }}
      </ProductIntroTile>
    </Sentry.ErrorBoundary>
  );
};
